import { apiClient } from './apiClient';
import type { Entity, EntityConnection } from '../../services/apiService';

// Helper to map entity response to frontend Entity interface
const mapEntityResponse = (entity: any): Entity => ({
  id: entity.uuid || entity.id,
  name: entity.primary_id || entity.name,
  concept_id: entity.component_type || entity.concept_id,
  site_id: entity.site_id,
  tenant_id: entity.tenant_id || '',
  parent_entity_id: entity.parent_entity_id || null,
  description: entity.description || null,
  manufacturer: entity.manufacturer || null,
  model: entity.model || null,
  serial_number: entity.serial_number || null,
  installation_date: entity.installation_date || null,
  warranty_expiry: entity.warranty_expiry || null,
  status: entity.status || 'active',
  latitude: entity.latitude || null,
  longitude: entity.longitude || null,
  altitude: entity.altitude || null,
  orientation: entity.orientation || null,
  tilt: entity.tilt || null,
  properties: entity.properties || {},
  version: entity.version || 1,
  created_at: entity.created_at || new Date().toISOString(),
  updated_at: entity.updated_at || new Date().toISOString(),
  // Legacy field mappings for compatibility
  entity_id: entity.uuid || entity.id,
  entity_name: entity.primary_id || entity.name,
  entity_type_id: entity.component_type || entity.concept_id,
  entity_type_name: entity.component_type || entity.concept_name,
  concept_name: entity.component_type || entity.concept_name,
  concept_category: entity.component_type || entity.concept_category,
  pvcollada_component_type: entity.component_type || entity.pvcollada_component_type,
  metadata: entity.properties || {},
  entity_metadata: entity.properties || {},
  component_order: entity.component_order,
  aliases: entity.aliases || [],
  configuration: entity.configuration || {},
  pref: entity.pref,
});

// Helper to map connection response to frontend EntityConnection interface
const mapConnectionResponse = (conn: any): EntityConnection => ({
  id: conn.id,
  tenant_id: conn.tenant_id || '',
  site_id: conn.site_id,
  source_entity_id: conn.source_entity_id,
  target_entity_id: conn.target_entity_id,
  connection_type: conn.relation || conn.connection_type || 'connects',
  properties: {
    ...(conn.properties || {}),
    signal: conn.signal,
    pref: conn.pref,
  },
  configuration: conn.configuration || {},
  created_at: conn.created_at || new Date().toISOString(),
  source_entity_name: conn.source_entity_name,
  target_entity_name: conn.target_entity_name,
});

export interface ListEntitiesParams {
  portfolio_id: string;
  site_id: string;
  limit?: number;
  offset?: number;
}

export interface ListConnectionsParams {
  portfolio_id: string;
  site_id: string;
  limit?: number;
  offset?: number;
}

export interface ListEntitiesIncrementalParams {
  portfolio_id: string;
  site_id: string;
  limit: number;
  offset: number;
  concept_id?: string;
}

export interface ListConnectionsForEntitiesParams {
  portfolio_id: string;
  site_id: string;
  entity_ids: string[];
}

export interface EntitiesResponse {
  items: Entity[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConnectionsResponse {
  items: EntityConnection[];
  total: number;
  limit: number;
  offset: number;
}

export const entityApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    // List entities for a site
    listEntities: builder.query<Entity[], ListEntitiesParams>({
      queryFn: async ({ portfolio_id, site_id }, _queryApi, _extraOptions, baseQuery) => {
        const pageSize = 1000; // Max allowed by backend
        let allEntities: Entity[] = [];
        let offset = 0;
        let total = 0;

        // Fetch first page to get total count
        const firstResult = await baseQuery({
          url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/entities`,
          method: 'GET',
          params: { limit: pageSize, offset: 0 },
        });

        if (firstResult.error) {
          return { error: firstResult.error };
        }

        // Handle response format - could be array or object with items
        let firstResponse: EntitiesResponse;
        const firstData = firstResult.data;
        
        if (Array.isArray(firstData)) {
          // If response is directly an array, treat as single page
          allEntities = firstData.map(mapEntityResponse);
          total = firstData.length;
          return { data: allEntities };
        } else if (firstData?.items && Array.isArray(firstData.items)) {
          firstResponse = firstData as EntitiesResponse;
          total = firstResponse.total || firstResponse.items.length;
          allEntities = firstResponse.items.map(mapEntityResponse);
        } else {
          return { data: [] };
        }

        // Fetch remaining pages if needed
        offset = pageSize;
        while (offset < total) {
          const pageResult = await baseQuery({
            url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/entities`,
            method: 'GET',
            params: { limit: pageSize, offset },
          });

          if (pageResult.error) {
            return { error: pageResult.error };
          }

          const pageData = pageResult.data;
          let pageItems: any[] = [];
          
          if (Array.isArray(pageData)) {
            pageItems = pageData;
          } else if (pageData?.items && Array.isArray(pageData.items)) {
            pageItems = pageData.items;
          }

          if (pageItems.length > 0) {
            allEntities = allEntities.concat(pageItems.map(mapEntityResponse));
          }

          offset += pageSize;
        }

        return { data: allEntities };
      },
      keepUnusedDataFor: 0, // Don't cache - always fetch fresh data
      providesTags: (result, error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}` },
        'Entity',
      ],
    }),

    // List connections for a site
    listConnections: builder.query<EntityConnection[], ListConnectionsParams>({
      queryFn: async ({ portfolio_id, site_id }, _queryApi, _extraOptions, baseQuery) => {
        const pageSize = 1000; // Max allowed by backend
        let allConnections: EntityConnection[] = [];
        let offset = 0;
        let total = 0;

        // Fetch first page to get total count
        const firstResult = await baseQuery({
          url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/connections`,
          method: 'GET',
          params: { limit: pageSize, offset: 0 },
        });

        if (firstResult.error) {
          return { error: firstResult.error };
        }

        // Handle response format - could be array or object with items
        let firstResponse: ConnectionsResponse;
        const firstData = firstResult.data;
        
        if (Array.isArray(firstData)) {
          // If response is directly an array, treat as single page
          allConnections = firstData.map(mapConnectionResponse);
          total = firstData.length;
          return { data: allConnections };
        } else if (firstData?.items && Array.isArray(firstData.items)) {
          firstResponse = firstData as ConnectionsResponse;
          total = firstResponse.total || firstResponse.items.length;
          allConnections = firstResponse.items.map(mapConnectionResponse);
        } else {
          return { data: [] };
        }

        // Fetch remaining pages if needed
        offset = pageSize;
        while (offset < total) {
          const pageResult = await baseQuery({
            url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/connections`,
            method: 'GET',
            params: { limit: pageSize, offset },
          });

          if (pageResult.error) {
            return { error: pageResult.error };
          }

          const pageData = pageResult.data;
          let pageItems: any[] = [];
          
          if (Array.isArray(pageData)) {
            pageItems = pageData;
          } else if (pageData?.items && Array.isArray(pageData.items)) {
            pageItems = pageData.items;
          }

          if (pageItems.length > 0) {
            allConnections = allConnections.concat(pageItems.map(mapConnectionResponse));
          }

          offset += pageSize;
        }

        return { data: allConnections };
      },
      keepUnusedDataFor: 0, // Don't cache - always fetch fresh data
      providesTags: (result, error, arg) => [
        { type: 'Connection', id: `${arg.portfolio_id}-${arg.site_id}` },
        'Connection',
      ],
    }),

    // Get a specific entity
    getEntity: builder.query<Entity, { entity_id: string }>({
      query: ({ entity_id }) => ({
        url: `/api/v1/entities/${entity_id}`,
        method: 'GET',
      }),
      transformResponse: (entity: any): Entity => {
        const mapped = mapEntityResponse(entity);
        // Add additional fields that may be needed for single entity queries
        return {
          ...mapped,
          asset_tag: entity.serial_number || mapped.serial_number,
          location: entity.latitude && entity.longitude ? {
            type: 'Point',
            coordinates: [entity.longitude, entity.latitude]
          } : null
        };
      },
    }),

    // Incremental loading: Fetch a subset of entities with pagination
    // Useful for initial load or loading entities in chunks
    listEntitiesIncremental: builder.query<EntitiesResponse, ListEntitiesIncrementalParams>({
      query: ({ portfolio_id, site_id, limit, offset, concept_id }) => ({
        url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/entities`,
        method: 'GET',
        params: {
          limit,
          offset,
          ...(concept_id && { concept_id }),
        },
      }),
      transformResponse: (response: any): EntitiesResponse => {
        // Handle response format
        if (Array.isArray(response)) {
          return {
            items: response.map(mapEntityResponse),
            total: response.length,
            limit: response.length,
            offset: 0,
          };
        } else if (response?.items && Array.isArray(response.items)) {
          return {
            items: response.items.map(mapEntityResponse),
            total: response.total || response.items.length,
            limit: response.limit || response.items.length,
            offset: response.offset || 0,
          };
        } else {
          return {
            items: [],
            total: 0,
            limit: 0,
            offset: 0,
          };
        }
      },
      keepUnusedDataFor: 0,
      providesTags: (result, error, arg) => [
        { type: 'Entity', id: `${arg.portfolio_id}-${arg.site_id}-incremental-${arg.offset}` },
        'Entity',
      ],
    }),

    // Incremental loading: Fetch connections for specific entity IDs
    // Useful for loading connections on-demand when entities become visible
    listConnectionsForEntities: builder.query<EntityConnection[], ListConnectionsForEntitiesParams>({
      queryFn: async ({ portfolio_id, site_id, entity_ids }, _queryApi, _extraOptions, baseQuery) => {
        // If no entity IDs provided, return empty array
        if (!entity_ids || entity_ids.length === 0) {
          return { data: [] };
        }

        // Fetch connections for each entity ID
        // Note: This assumes the backend supports filtering by entity_id
        // If not, we may need to fetch all and filter client-side
        const allConnections: EntityConnection[] = [];
        const pageSize = 1000;

        for (const entityId of entity_ids) {
          let offset = 0;
          let hasMore = true;

          while (hasMore) {
            const result = await baseQuery({
              url: `/api/v1/portfolios/${portfolio_id}/sites/${site_id}/connections`,
              method: 'GET',
              params: {
                limit: pageSize,
                offset,
                entity_id: entityId,
              },
            });

            if (result.error) {
              // Log error but continue with other entities
              console.warn(`Error fetching connections for entity ${entityId}:`, result.error);
              break;
            }

            const data = result.data;
            let items: any[] = [];

            if (Array.isArray(data)) {
              items = data;
            } else if (data?.items && Array.isArray(data.items)) {
              items = data.items;
            }

            if (items.length > 0) {
              const connections = items
                .map(mapConnectionResponse)
                .filter(
                  conn =>
                    conn.source_entity_id === entityId ||
                    conn.target_entity_id === entityId
                );
              allConnections.push(...connections);
            }

            hasMore = items.length === pageSize;
            offset += pageSize;
          }
        }

        // Remove duplicates (connections might appear for both source and target)
        const uniqueConnections = Array.from(
          new Map(allConnections.map(conn => [conn.id, conn])).values()
        );

        return { data: uniqueConnections };
      },
      keepUnusedDataFor: 0,
      providesTags: (result, error, arg) => [
        { type: 'Connection', id: `${arg.portfolio_id}-${arg.site_id}-for-entities` },
        'Connection',
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useListEntitiesQuery,
  useLazyListEntitiesQuery,
  useListConnectionsQuery,
  useLazyListConnectionsQuery,
  useGetEntityQuery,
  useListEntitiesIncrementalQuery,
  useLazyListEntitiesIncrementalQuery,
  useListConnectionsForEntitiesQuery,
  useLazyListConnectionsForEntitiesQuery,
} = entityApi;

